import { memo, type ReactNode, Ref } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Center, Pill, VideoPlayerHandle, WithSidebar } from 'src/components';
import { isLiteralTranslationLabel, isRomanizationLabel } from 'src/api/settings/learningToggles';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import { LearnableRowInfo, TemplateName, TestPromptType } from 'src/api/learningSessions/types';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { LearnableValue } from 'src/api/learningSessions/schema';
import ResponseDisplay, { TestResponse } from './Response';
import PromptDisplay, { TestPrompt } from './Prompt';
import * as S from './styles';
import { getLargeScreenLayout } from './utils';

// Workaround for re-exported type ban
// https://github.com/facebook/create-react-app/issues/6054#issuecomment-481441272
export type Prompt = TestPrompt;
export type Response = TestResponse;

const msgs = defineMessages({
  textToMCInstruction: {
    id: 'text_mc_test_instruction',
  },
  textToTypingInstruction: {
    id: 'learning_session_learnable_card_text_typing_instruction',
  },
  audioToMCInstruction: {
    id: 'audio_mc_test_instruction',
  },
  audioToRMCInstruction: {
    id: 'audio_rmc_test_instruction',
  },
  audioToTappingInstruction: {
    id: 'audio_tapping_test_instruction',
  },
  audioToTypingInstruction: {
    id: 'learning_session_learnable_card_audio_typing_instruction',
  },
  videoToMCInstruction: {
    id: 'video_mc_test_instruction',
  },
  videoToRMCInstruction: {
    id: 'video_mc_reverse_test_instruction',
  },
  videoToTappingInstruction: {
    id: 'video_tapping_instruction',
  },
  videoToTypingInstruction: {
    id: 'video_typing_target_instruction',
  },
});

export const getInstructionMsg = (
  promptType: TestPromptType,
  templateName: Exclude<TemplateName, TemplateName.PRESENTATION>,
): MessageDescriptor | null => {
  if (promptType === 'Any' || promptType === null) return null;
  const msgLookup: Record<
    Exclude<TestPromptType, null | 'Any'>,
    Record<Exclude<TemplateName, TemplateName.PRESENTATION>, MessageDescriptor | null>
  > = {
    Text: {
      [TemplateName.MULTIPLE_CHOICE]: msgs.textToMCInstruction,
      [TemplateName.REVERSED_MULTIPLE_CHOICE]: msgs.textToMCInstruction,
      [TemplateName.AUDIO_MULTIPLE_CHOICE]: msgs.textToMCInstruction,
      [TemplateName.TAPPING]: msgs.textToMCInstruction,
      [TemplateName.TYPING]: msgs.textToTypingInstruction,
      [TemplateName.TRACING]: null,
    },
    Audio: {
      [TemplateName.MULTIPLE_CHOICE]: msgs.audioToMCInstruction,
      [TemplateName.REVERSED_MULTIPLE_CHOICE]: msgs.audioToRMCInstruction,
      [TemplateName.AUDIO_MULTIPLE_CHOICE]: null,
      [TemplateName.TAPPING]: msgs.audioToTappingInstruction,
      [TemplateName.TYPING]: msgs.audioToTypingInstruction,
      [TemplateName.TRACING]: null,
    },
    Video: {
      [TemplateName.MULTIPLE_CHOICE]: msgs.videoToMCInstruction,
      [TemplateName.REVERSED_MULTIPLE_CHOICE]: msgs.videoToRMCInstruction,
      [TemplateName.AUDIO_MULTIPLE_CHOICE]: null,
      [TemplateName.TAPPING]: msgs.videoToTappingInstruction,
      [TemplateName.TYPING]: msgs.videoToTypingInstruction,
      [TemplateName.TRACING]: null,
    },
    Image: {
      [TemplateName.MULTIPLE_CHOICE]: null,
      [TemplateName.REVERSED_MULTIPLE_CHOICE]: null,
      [TemplateName.AUDIO_MULTIPLE_CHOICE]: null,
      [TemplateName.TAPPING]: null,
      [TemplateName.TYPING]: null,
      [TemplateName.TRACING]: null,
    },
  };
  return msgLookup[promptType][templateName];
};

export const getPostAnswerDisplayedInfo = ({
  prompt,
  response,
  item,
  definition,
  postAnswerInfo,
}: Pick<
  TestLearnableCardProps,
  'prompt' | 'response' | 'item' | 'definition' | 'postAnswerInfo'
>) => {
  if (
    prompt.direction === 'source' &&
    prompt.promptType === 'Text' &&
    response.templateName === TemplateName.AUDIO_MULTIPLE_CHOICE
  ) {
    return item;
  }

  if (prompt.direction === 'target' && response.direction === 'target') {
    return definition;
  }

  return postAnswerInfo;
};

interface TestLearnableCardProps {
  showInstruction?: boolean;
  prompt: Prompt;
  response: TestResponse;
  attributeValues: string[];
  postAnswerInfo?: LearnableRowInfo;
  responseState: ResponseState;
  userAnswer: string;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  definition?: LearnableValue;
  item?: LearnableValue;
  showLiteralTranslation?: boolean;
  showRomanization?: boolean;
  videoControlRef?: Ref<VideoPlayerHandle>;
  /** Video tests: next action (e.g. I don't know) sits with the options. */
  footerAction?: ReactNode;
  /** Mobile video tests: replay / difficult / IAKT / like strip just above the options. */
  footerToolbar?: ReactNode;
}

const TestLearnableCard = ({
  showInstruction = true,
  prompt,
  response,
  attributeValues,
  postAnswerInfo,
  responseState,
  userAnswer,
  learningSessionDispatch,
  definition,
  item,
  showLiteralTranslation = true,
  showRomanization = true,
  videoControlRef,
  footerAction,
  footerToolbar,
}: TestLearnableCardProps) => {
  const intl = useIntl();
  const literalTranslationLabel = intl.formatMessage({ id: 'literal_translation' });
  const instruction = getInstructionMsg(prompt.promptType, response.templateName);
  const isPromptText = prompt.promptType === 'Text';
  const isFullScreenVideo = prompt.promptType === 'Video';
  const contentMinWidth = isPromptText ? '100%' : '50%';

  const showFeedback = ['Correct', 'Incorrect', 'NearlyCorrect'].includes(responseState);
  const resolvedPostAnswerInfo =
    postAnswerInfo &&
    (showLiteralTranslation ||
      !isLiteralTranslationLabel(postAnswerInfo.label, literalTranslationLabel)) &&
    (showRomanization || !isRomanizationLabel(postAnswerInfo.label))
      ? postAnswerInfo
      : undefined;
  const postAnswerDisplayedInfo =
    showInstruction && showFeedback
      ? getPostAnswerDisplayedInfo({
          prompt,
          response,
          item,
          definition,
          postAnswerInfo: resolvedPostAnswerInfo,
        })
      : null;

  const attributes =
    attributeValues.length > 0 ? (
      <S.AttributesContainer>
        {attributeValues.map((attributeValue, attributeIndex) => {
          return (
            <S.Attribute
              // eslint-disable-next-line react/no-array-index-key
              key={`attribute-${attributeValue}-${attributeIndex}`}
            >
              <Pill color="green">{attributeValue}</Pill>
            </S.Attribute>
          );
        })}
      </S.AttributesContainer>
    ) : null;

  const responseDisplay = (
    <ResponseDisplay
      response={response}
      largeScreenLayout={getLargeScreenLayout(prompt, response)}
      responseState={responseState}
      userAnswer={userAnswer}
      learningSessionDispatch={learningSessionDispatch}
      onVideo={isFullScreenVideo}
    />
  );

  const info = (
    <S.InfoContainer>
      {showInstruction && !postAnswerDisplayedInfo && instruction !== null && (
        <S.Instruction $onVideo={isFullScreenVideo} data-testid="instruction">
          {intl.formatMessage(instruction)}
        </S.Instruction>
      )}
      {postAnswerDisplayedInfo && (
        <S.PostAnswerInfo>
          <S.PostAnswerLabel>{postAnswerDisplayedInfo.label}</S.PostAnswerLabel>
          <span
            dir="auto"
            {...(isRomanizationLabel(postAnswerDisplayedInfo.label)
              ? romanizationTextAttrs(postAnswerDisplayedInfo.value as string)
              : undefined)}
          >
            {postAnswerDisplayedInfo.value as string}
          </span>
        </S.PostAnswerInfo>
      )}
    </S.InfoContainer>
  );

  return (
    <S.Container data-testid="testLearnableCard" $fullScreenVideo={isFullScreenVideo}>
      {isFullScreenVideo && <PromptDisplay prompt={prompt} videoRef={videoControlRef} />}
      {isFullScreenVideo ? (
        <>
          {info}
          {attributes}
          <S.VideoTestFooter data-testid="video-test-footer">
            {footerToolbar ? (
              <S.VideoTestToolbar data-testid="video-test-toolbar">
                {footerToolbar}
              </S.VideoTestToolbar>
            ) : null}
            {footerAction ? (
              <S.VideoTestNextAction
                data-testid="video-test-next-action"
                $belowResponseOnMobile={response.templateName === TemplateName.TYPING}
              >
                {footerAction}
              </S.VideoTestNextAction>
            ) : null}
            <S.VideoTestColumn data-testid="video-test-options">
              {responseDisplay}
            </S.VideoTestColumn>
          </S.VideoTestFooter>
        </>
      ) : (
        <>
          {info}
          <WithSidebar gap="s2" $contentMinWidth={contentMinWidth}>
            <S.PromptAndResponseContainer
              $isGridLayout={response.templateName === TemplateName.TYPING}
            >
              <Center $centerAlignItems>
                <PromptDisplay prompt={prompt} />
                {attributes}
              </Center>
              {responseDisplay}
            </S.PromptAndResponseContainer>
          </WithSidebar>
        </>
      )}
    </S.Container>
  );
};

export default memo(TestLearnableCard);
